import React from 'react';
import { X, CheckCircle, XCircle } from 'lucide-react';

const DetailedResultsModal = ({ 
  testResults, 
  testType, 
  difficulty, 
  formatTime, 
  setShowDetailedResults, 
  resetTest 
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-purple-800 flex items-center justify-center p-2 sm:p-4">
      <div className="max-w-4xl w-full bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden mx-2">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Test Results Overview</h2>
              <p className="text-sm sm:text-base text-gray-600 mt-1 capitalize">
                {testType.replace('_', ' ')} - {difficulty} difficulty
              </p>
            </div>
            <button
              onClick={() => setShowDetailedResults(false)}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mt-4">
            <div className="text-center p-2 sm:p-3 bg-white rounded-lg">
              <div className="text-xl sm:text-2xl font-bold text-blue-600">{testResults.correct}</div>
              <div className="text-xs sm:text-sm text-gray-500">Correct</div>
            </div>
            <div className="text-center p-2 sm:p-3 bg-white rounded-lg">
              <div className="text-xl sm:text-2xl font-bold text-gray-600">{testResults.total}</div>
              <div className="text-xs sm:text-sm text-gray-500">Total</div>
            </div>
            <div className="text-center p-2 sm:p-3 bg-white rounded-lg">
              <div className="text-xl sm:text-2xl font-bold text-green-600">{testResults.accuracy}%</div>
              <div className="text-xs sm:text-sm text-gray-500">Accuracy</div>
            </div>
            <div className="text-center p-2 sm:p-3 bg-white rounded-lg">
              <div className="text-xl sm:text-2xl font-bold text-purple-600">{formatTime(testResults.timeSpent)}</div>
              <div className="text-xs sm:text-sm text-gray-500">Time</div>
            </div>
          </div>
        </div>

        {/* Questions List */}
        <div className="overflow-y-auto max-h-96 p-4 sm:p-6">
          <div className="space-y-4">
            {testResults.detailedResults.map((result, index) => (
              <div
                key={index}
                className={`border-2 rounded-xl p-3 sm:p-4 ${
                  result.isCorrect 
                    ? 'border-green-200 bg-green-50' 
                    : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm font-medium text-black">
                      Question {index + 1}
                    </span>
                    {result.isCorrect ? (
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                    )}
                  </div>
                  {result.firmTag && (
                    <span className="text-xs px-2 py-1 bg-gray-200 rounded-full text-gray-600">
                      {result.firmTag}
                    </span>
                  )}
                </div>
                
                <div className="text-sm sm:text-base text-gray-800 font-medium mb-3">
                  {result.question}
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-black mb-1">Your Answer:</div>
                    <div className={`p-2 rounded bg-gray-100 text-xs sm:text-sm ${
                      result.userAnswer === 'No answer' ? 'text-gray-500 italic' : 'text-gray-800'
                    }`}>
                      {result.userAnswer}
                    </div>
                  </div>
            
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-black mb-1">Correct Answer:</div>
                    <div className="p-2 rounded bg-green-100 text-xs sm:text-sm text-gray-800">
                      {result.correctAnswer}
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs sm:text-sm font-medium">Result:</span>
                  <span className={`text-xs sm:text-sm font-medium ${
                    result.isCorrect ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {result.isCorrect ? 'Correct' : 'Incorrect'}
                  </span>
                </div>

                {result.explanation && (
                  <>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs sm:text-sm font-medium">
                        Explanation:
                      </span>
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 font-medium mb-1">
                      {result.explanation}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setShowDetailedResults(false)}
              className="px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors text-sm sm:text-base"
            >
              Close
            </button>
            <button
              onClick={resetTest}
              className="px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transform hover:-translate-y-0.5 transition-all text-sm sm:text-base"
            >
              Take Another Test
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedResultsModal;